/**
 * Announcement bar marquee.
 *
 * header-announcements.liquid renders the announcement blocks once, inside
 * `.announcement-marquee__group`. This element clones that group until the row
 * is wider than the bar and sets the animation duration from the group's own
 * width, so the CSS `translateX(-100%)` loop reads as one continuous line at a
 * constant speed no matter how much text the client puts in.
 */

const DEFAULT_SPEED = 60; // px per second

class AnnouncementMarquee extends HTMLElement {
  #frame = 0;
  #resizeObserver;

  connectedCallback() {
    this.#schedule();

    this.#resizeObserver = new ResizeObserver(() => this.#schedule());
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame);
    this.#resizeObserver?.disconnect();
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#build());
  }

  #build() {
    const group = this.querySelector('[data-marquee-group]');

    if (!group) return;

    for (const clone of this.querySelectorAll('[data-marquee-clone]')) clone.remove();

    const groupWidth = group.getBoundingClientRect().width;
    const trackWidth = this.getBoundingClientRect().width;

    // Fonts or images may not have settled yet; a zero-width group would give
    // a zero-second animation, so wait for the next resize instead.
    if (groupWidth < 1) return;

    const speed = Number.parseFloat(this.dataset.speed) || DEFAULT_SPEED;

    this.style.setProperty('--announcement-marquee-duration', `${groupWidth / speed}s`);

    const copies = Math.ceil(trackWidth / groupWidth) + 1;
    const fragment = document.createDocumentFragment();

    for (let index = 0; index < copies; index++) {
      const clone = /** @type {HTMLElement} */ (group.cloneNode(true));

      clone.setAttribute('data-marquee-clone', '');
      clone.removeAttribute('data-marquee-group');
      // The copies are the same words again: keep them out of the accessibility
      // tree, and out of the theme editor's block map.
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');

      for (const node of clone.querySelectorAll('[data-shopify-editor-block], [id]')) {
        node.removeAttribute('data-shopify-editor-block');
        node.removeAttribute('id');
      }

      for (const link of clone.querySelectorAll('a')) link.setAttribute('tabindex', '-1');

      fragment.appendChild(clone);
    }

    this.appendChild(fragment);

    // The original group has been animating since the page loaded and the
    // clones start now, so without a restart they run out of phase and the
    // line shows gaps. Dropping and re-adding the animation on all of them in
    // one reflow puts every copy back on the same clock.
    const groups = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.announcement-marquee__group')]);

    for (const item of groups) item.style.animation = 'none';

    void this.offsetWidth;

    for (const item of groups) item.style.animation = '';
  }
}

if (!customElements.get('announcement-marquee')) {
  customElements.define('announcement-marquee', AnnouncementMarquee);
}
